import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import { FiArrowLeft } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { Alert, Skeleton } from '@material-ui/lab';
import CustomStore from 'devextreme/data/custom_store';
import { addDays, addWeeks, format, startOfWeek } from 'date-fns';
import { List as ListLoader } from 'react-content-loader';

import api from '../../../services/api';
import { errorMessage$, successMessage$ } from '../../../utils/subjects';
import InputInlineEdit from '../../../components/InputInlineEdit';
import SelectInlineEdit from '../../../components/SelectInlineEdit';
import PendingProjectAttach from './PendingProjectAttach';
import SoldValue from './SoldValue';
import PendingProjectCosts from './PendingProjectCosts';
import { dateOnly } from './dates';
import '../../../components/ToolsMenu/Tabs/styles.css';

// Limite da coluna Project.ProjectCode. O inclient aceita até 100, então o aviso
// aparece aqui e a aprovação recusa.
const PROJECT_CODE_MAX_LENGTH = 10;

const PATHS = [
  { ID: 'Create', Name: 'New project' },
  { ID: 'Attach', Name: 'Attach to an existing project' },
];

function lookupStore(url, params, mapper) {
  return {
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get(url, params ? { params } : undefined);
        return mapper ? response.data.map(mapper) : response.data;
      },
    }),
    paginate: true,
  };
}

function formatDate(value) {
  return value ? format(dateOnly(value), 'dd MMM yyyy') : '';
}

function formatNumber(value) {
  return value === null || value === undefined
    ? '-'
    : Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
}

function Row({ children }) {
  return <div className="project-info-box">{children}</div>;
}

function ReadOnly({ caption, value, loading }) {
  return (
    <div className="list-item-content">
      <span className="secondary-text">{caption}: </span>
      {loading ? (
        <Skeleton variant="text" width={160} style={{ display: 'inline-block' }} />
      ) : (
        <span>{value}</span>
      )}
    </div>
  );
}

// O SelectInlineEdit/InputInlineEdit salvam ao perder o foco mesmo sem mudança;
// comparar evita um PUT (e o "Saving…") à toa.
function sameValue(a, b) {
  const empty = v => v === null || v === undefined || v === '';
  if (empty(a) || empty(b)) return empty(a) && empty(b);
  return String(a) === String(b);
}

// Espelho do Project Info para uma ordem pendente: cada campo salva ao sair da
// edição. O vínculo com a proposta não aparece como campo — não é editável.
export default function PendingProjectInfo({ id, open, handleClose }) {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [approving, setApproving] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [approvalWarnings, setApprovalWarnings] = useState(null);

  const [clientSource] = useState(() =>
    lookupStore('master/clients', { where: { IsActive: true } }),
  );
  const [businessUnitSource] = useState(() =>
    lookupStore('master/business-units'),
  );
  const [currencySource] = useState(() =>
    lookupStore('master/currencies', null, currency => ({
      ...currency,
      DisplayName: `${currency.Name} - ${currency.Abbreviation}`,
    })),
  );
  const [professionalSource] = useState(() =>
    lookupStore('master/professionals', { where: { IsActive: true } }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`scheduling/project-orders/${id}`);
      setDetail(response.data);
    } catch (err) {
      errorMessage$.next({ open: true, message: 'Error loading pending project' });
      handleClose();
    } finally {
      setLoading(false);
    }
  }, [id, handleClose]);

  useEffect(() => {
    load();
  }, [load]);

  // Campos com PUT em andamento (um por save; o mesmo campo pode repetir). Enquanto
  // houver algum, o painel avisa "Saving…" e aprovar/anexar fica bloqueado.
  const [savingFields, setSavingFields] = useState([]);
  const detailRef = useRef(null);
  detailRef.current = detail;

  const save = useCallback(
    async rawChanges => {
      const current = detailRef.current || {};
      // Ordem já decidida: nada é enviado (a api recusaria com 409 de qualquer jeito).
      if (current.Status && current.Status !== 'Pending') return;
      const changes = {};
      Object.keys(rawChanges).forEach(field => {
        if (!sameValue(rawChanges[field], current[field])) changes[field] = rawChanges[field];
      });
      const fields = Object.keys(changes);
      if (fields.length === 0) return;

      setSavingFields(saving => [...saving, ...fields]);
      try {
        const response = await api.put(`scheduling/project-orders/${id}`, changes);
        setDetail(response.data);
      } catch (err) {
        // 400/403/500 já viram mensagem no interceptor; 409 (linha já decidida) não.
        if (err.response && err.response.status === 409) {
          errorMessage$.next({ open: true, message: err.response.data.message });
          load();
        }
      } finally {
        setSavingFields(saving => {
          const next = [...saving];
          fields.forEach(field => {
            const index = next.indexOf(field);
            if (index >= 0) next.splice(index, 1);
          });
          return next;
        });
      }
    },
    [id, load],
  );

  const saving = savingFields.length > 0;
  const isSaving = (...fields) => fields.some(field => savingFields.includes(field));

  // Aprovar cria o projeto (Draft, com IDProposal) e leva o staffing para a
  // alocação dele. Validação e motivo são conferidos de novo no servidor.
  const approve = useCallback(
    async approvalReason => {
      setApproving(true);
      try {
        const response = await api.post(`scheduling/project-orders/${id}/approve`, {
          Reason: approvalReason || null,
        });
        const { IDProject, Warnings } = response.data;

        // Com aviso (margem não calculada, vendas não notificada), o staffing precisa
        // ler antes de seguir — um toast some sozinho.
        if (Warnings && Warnings.length > 0) {
          setApprovalWarnings({ projectId: IDProject, messages: Warnings });
          return;
        }

        successMessage$.next({ open: true, message: 'Project created' });
        history.push(`/projects/${IDProject}/allocations`);
      } catch (err) {
        // 400 (o que falta, código em uso, motivo) já vira mensagem no interceptor.
        if (err.response && err.response.status === 409) {
          errorMessage$.next({ open: true, message: err.response.data.message });
          handleClose();
        }
        setApproving(false);
      }
    },
    [id, history, handleClose],
  );

  // Lista completa: com uma opção só na venda, a function já preencheu o campo.
  const options = useMemo(() => {
    const withLabel = list =>
      (list || []).map(option => ({ ...option, DisplayName: option.Name }));

    return {
      practices: withLabel(detail && detail.Options.Practices),
      offices: withLabel(detail && detail.Options.Offices),
      geographics: withLabel(detail && detail.Options.Geographics),
    };
  }, [detail]);

  if (!detail || loading) {
    return (
      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box m={2} width={560}>
          <ListLoader speed={2} />
        </Box>
      </Drawer>
    );
  }

  const names = detail.Names;
  const isAttach = detail.Kind === 'Attach';
  const salesCode = detail.SalesProjectCode;

  // Total dos custos da venda por moeda — moedas diferentes nunca se somam.
  const costTotals = new Map();
  (detail.AdditionalCosts || []).forEach(cost => {
    const currency = cost.CurrencyAbbreviation || '';
    costTotals.set(currency, (costTotals.get(currency) || 0) + Number(cost.TotalValue));
  });
  const embeddedExpense =
    costTotals.size === 0
      ? '-'
      : [...costTotals.entries()]
          .map(([currency, total]) => `${currency} ${formatNumber(total)}`.trim())
          .join(' · ');
  const codeDiffers = !!salesCode && !!detail.ProjectCode && salesCode !== detail.ProjectCode;
  const codeTooLong =
    !!detail.ProjectCode && detail.ProjectCode.length > PROJECT_CODE_MAX_LENGTH;

  // Semana do Scheduling começa no DOMINGO (ScheduleWeek); a tela mostra até a
  // sexta da última semana, como o cabeçalho do projeto. Contar pela segunda
  // errava uma semana quando o início caía num domingo.
  const startWeek = detail.StartDate
    ? startOfWeek(dateOnly(detail.StartDate), { weekStartsOn: 0 })
    : null;
  const endDate =
    startWeek && detail.Weeks
      ? format(addDays(addWeeks(startWeek, detail.Weeks), -2), 'dd MMM yyyy')
      : '';

  // Ordem já decidida (por outra aba ou pessoa) com o painel ainda aberto: só
  // leitura, sem aprovar nem anexar.
  const readOnly = detail.Status !== 'Pending';

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box m={2} width={560}>
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Back to the list"
            style={{ marginRight: 8 }}
          >
            <FiArrowLeft />
          </IconButton>
          <Typography variant="h5" component="h5" style={{ fontWeight: 500 }}>
            Sold project
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {saving ? (
            <>
              <CircularProgress size={12} color="inherit" />
              Saving…
            </>
          ) : (
            'Review what came from the sale. Changes are saved as you go.'
          )}
        </Typography>
        {readOnly && (
          <Alert severity="info" style={{ margin: '0 0 8px' }}>
            This order is already {detail.Status} — it can no longer be changed here.
          </Alert>
        )}

        <div className="big-numbers">
          <div className="project-info-box numbers">
            {/* Sem despesa embutida: o Scheduling agrega custos à parte. */}
            <h3>Sold value (net)</h3>
            <strong>
              {detail.ProjectValueInitial === null || detail.ProjectValueInitial === undefined
                ? '-'
                : `${names.CurrencyAbbreviation || ''} ${formatNumber(
                    detail.ProjectValueInitial,
                  )}`.trim()}
            </strong>
          </div>
          <div className="project-info-box numbers">
            {/* Na anexação a margem da extensão só é mostrada: a MarginSale do
                projeto de destino continua a da venda original. */}
            <h3>{isAttach ? 'Margin sale (this extension)' : 'Margin sale'}</h3>
            <strong>
              {detail.MarginSale === null || detail.MarginSale === undefined
                ? '-'
                : `${Number(detail.MarginSale).toFixed(2)}%`}
            </strong>
          </div>
          <div className="project-info-box numbers">
            {/* Soma dos custos da venda (despesa embutida), na moeda deles. */}
            <h3>Embedded expense</h3>
            <strong>{embeddedExpense}</strong>
          </div>
        </div>

        {/* O Kind do inclient é sugestão: quem aprova decide o caminho. */}
        <Row>
          <SelectInlineEdit
            value={detail.Kind}
            // Kind fora da lista não derruba o painel: mostra como New project.
            displayValue={(PATHS.find(path => path.ID === detail.Kind) || PATHS[0]).Name}
            dataField="Kind"
            caption="Path"
            displayExpr="Name"
            valueExpr="ID"
            dataSource={PATHS}
            onChange={save}
          />
        </Row>
        <Row>
          <ReadOnly
            caption="Proposal type"
            value={detail.IsExtension ? 'Extension' : 'New'}
          />
        </Row>

        {/* Trocar o caminho troca o formulário inteiro: até o servidor responder, o
            que está na tela é do caminho antigo — skeleton em vez dele. */}
        {isSaving('Kind') ? (
          <Box mt={1}>
            <ListLoader speed={2} />
          </Box>
        ) : isAttach ? (
          <PendingProjectAttach
            detail={detail}
            save={save}
            saving={saving}
            readOnly={readOnly}
            handleClose={handleClose}
          />
        ) : (
          <>
        <Row>
          <InputInlineEdit
            value={detail.ProjectName || ''}
            dataField="ProjectName"
            caption="Project Name"
            onChange={save}
          />
        </Row>
        <Row>
          <InputInlineEdit
            value={detail.ProjectCode || ''}
            dataField="ProjectCode"
            caption="Project Code"
            onChange={save}
          />
        </Row>
        {codeDiffers && (
          <Alert severity="warning" style={{ margin: 5 }}>
            Sales registered <strong>{salesCode}</strong>. The project will be
            created as <strong>{detail.ProjectCode}</strong> and sales will be
            informed.
          </Alert>
        )}
        {codeTooLong && (
          <Alert severity="error" style={{ margin: 5 }}>
            Project codes in Scheduling have at most {PROJECT_CODE_MAX_LENGTH}{' '}
            characters.
          </Alert>
        )}
        <Row>
          <SelectInlineEdit
            value={detail.IDClient}
            displayValue={names.Client}
            dataField="IDClient"
            caption="Client"
            displayExpr="Name"
            valueExpr="ID"
            dataSource={clientSource}
            onChange={save}
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDBusinessUnit}
            displayValue={names.BusinessUnit}
            dataField="IDBusinessUnit"
            caption="Business Unit"
            displayExpr="Name"
            valueExpr="ID"
            dataSource={businessUnitSource}
            onChange={save}
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDPractice}
            displayValue={names.Practice}
            dataField="IDPractice"
            caption="Practice"
            displayExpr="DisplayName"
            valueExpr="ID"
            dataSource={options.practices}
            onChange={save}
          />
        </Row>
        <Row>
          <ReadOnly
            caption="Co-management"
            loading={isSaving('IDPractice')}
            value={
              names.Comanagement ||
              (detail.IDPractice
                ? 'This practice has no co-management — choose another'
                : 'Choose a practice')
            }
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDProfessionalManager}
            displayValue={names.Manager}
            dataField="IDProfessionalManager"
            caption="Manager"
            displayExpr="Name"
            valueExpr="ID"
            dataSource={professionalSource}
            onChange={save}
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDProfessionalPartner}
            displayValue={names.Partner}
            dataField="IDProfessionalPartner"
            caption="Partner"
            displayExpr="Name"
            valueExpr="ID"
            dataSource={professionalSource}
            onChange={save}
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDOffice}
            displayValue={names.Office}
            dataField="IDOffice"
            caption="Office"
            displayExpr="DisplayName"
            valueExpr="ID"
            dataSource={options.offices}
            onChange={save}
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDBillingOffice}
            displayValue={names.BillingOffice}
            dataField="IDBillingOffice"
            caption="Billing Office"
            displayExpr="DisplayName"
            valueExpr="ID"
            dataSource={options.offices}
            onChange={save}
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDGeographic}
            displayValue={names.Geographic}
            dataField="IDGeographic"
            caption="Geographic"
            displayExpr="DisplayName"
            valueExpr="ID"
            dataSource={options.geographics}
            onChange={save}
          />
        </Row>
        <Row>
          <InputInlineEdit
            value={detail.CityState || ''}
            dataField="CityState"
            caption="City/State"
            onChange={save}
          />
        </Row>
        <Row>
          <SelectInlineEdit
            value={detail.IDCurrency}
            displayValue={names.Currency}
            dataField="IDCurrency"
            caption="Currency"
            displayExpr="DisplayName"
            valueExpr="ID"
            dataSource={currencySource}
            onChange={save}
          />
        </Row>
        <Row>
          <InputInlineEdit
            type="date"
            value={detail.StartDate ? format(dateOnly(detail.StartDate), 'yyyy-MM-dd') : ''}
            displayValue={formatDate(detail.StartDate)}
            dataField="StartDate"
            caption="Start Date"
            // Meio-dia evita que o fuso desloque a data para o dia anterior.
            onChange={({ StartDate }) =>
              save({ StartDate: StartDate ? `${StartDate}T12:00:00` : null })
            }
          />
        </Row>
        <Row>
          <InputInlineEdit
            type="number"
            value={detail.Weeks || ''}
            dataField="Weeks"
            caption="Weeks"
            inputProps={{ inputProps: { min: 1 } }}
            onChange={save}
          />
        </Row>
        <Row>
          <ReadOnly
            caption="End Date"
            value={endDate}
            loading={isSaving('StartDate', 'Weeks')}
          />
        </Row>
        <Row>
          <InputInlineEdit
            type="number"
            value={detail.ProjectValueInitial ?? ''}
            displayValue={
              <SoldValue
                amount={detail.ProjectValueInitial}
                currency={names.CurrencyAbbreviation}
                mainAmount={detail.ProjectValueInitialMain}
              />
            }
            dataField="ProjectValueInitial"
            caption="Project Initial Value"
            onChange={save}
          />
        </Row>

        <PendingProjectCosts costs={detail.AdditionalCosts} />

        {/* Sair mantém a ordem pendente, com tudo o que já foi salvo campo a campo. */}
        <Box mt={2} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          <Button variant="outlined" onClick={handleClose} disabled={approving}>
            Keep pending
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={approving || saving || codeTooLong || readOnly}
            onClick={() => (codeDiffers ? setReasonOpen(true) : approve(null))}
          >
            {approving ? <CircularProgress size={22} color="inherit" /> : 'Approve'}
          </Button>
        </Box>
          </>
        )}
      </Box>

      <Dialog open={reasonOpen} onClose={() => setReasonOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Project code differs from sales</DialogTitle>
        <DialogContent>
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            Sales registered <strong>{salesCode}</strong>. This project will be
            created as <strong>{detail.ProjectCode}</strong>, and sales will be
            informed of the change.
          </Alert>
          <TextField
            label="Reason"
            required
            fullWidth
            multiline
            rows={3}
            value={reason}
            inputProps={{ maxLength: 500 }}
            onChange={e => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!reason.trim() || approving}
            onClick={() => {
              setReasonOpen(false);
              approve(reason.trim());
            }}
          >
            Approve with this code
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!approvalWarnings} maxWidth="sm" fullWidth>
        <DialogTitle>Project created, with warnings</DialogTitle>
        <DialogContent>
          {approvalWarnings &&
            approvalWarnings.messages.map(message => (
              <Alert key={message} severity="warning" style={{ marginBottom: 8 }}>
                {message}
              </Alert>
            ))}
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() =>
              history.push(`/projects/${approvalWarnings.projectId}/allocations`)
            }
          >
            Open the project
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
